import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Curated founding signatories — always shown first, ahead of public signatories.
const FOUNDING_SIGNATORIES = [
  { name: 'Nyree Pearson', detail: 'Co-Founder, Safe Standard — Australia' },
  { name: 'Dr. Mashilo Boloka', detail: 'Online Safety Lab — South Africa' },
  { name: 'Dr. Loy Ehlers', detail: 'ChildSafe.dev — United States' },
  { name: 'Emmanuel Adinkra', detail: 'Ghana Internet Safety Foundation — Ghana' },
  { name: 'Professor Dr. M.K. Bhandari', detail: 'GALTER — India' },
  { name: 'Bianca Garibaldi', detail: 'Trust & Safety Specialist — Brazil' },
  { name: 'Mohamed ElBendary', detail: 'AI Governance Researcher — United States' },
  { name: 'Ryan James Purdy', detail: 'Purdy House Publishing & Consulting — Canada' },
];

const LegalModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-slate-900 text-white p-6">
          <h2 className="text-lg font-bold">{title}</h2>
        </div>
        <div className="p-6 md:p-8 text-sm text-slate-700 leading-relaxed space-y-4">
          {children}
        </div>
        <div className="p-6 border-t border-slate-200">
          <button onClick={onClose} className="btn-primary w-full">Close</button>
        </div>
      </div>
    </div>
  );
};

const OpenLetterPage = () => {
  const navigate = useNavigate();
  const [signatories, setSignatories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showLegal, setShowLegal] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', country: '', phone_code: '+1', phone: '',
    organization: '', role: '', industry: '', industry_other: '', linkedin: '', website: '', why_signing: '',
    heard_about: '', heard_about_other: '',
    confirm_read: false, confirm_no_legal: false, confirm_data: false,
    public_display: false, receive_updates: false,
  });

  useEffect(() => {
    axios.get(`${API}/open-letter/signatories`).then(res => {
      setSignatories(res.data.signatories);
      setTotalCount(res.data.total_count);
    }).catch(() => {});
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.confirm_read || !form.confirm_no_legal || !form.confirm_data) {
      toast.error('Please confirm all required checkboxes.');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        ...form,
        industry: form.industry === 'Other' ? form.industry_other : form.industry,
        heard_about: form.heard_about === 'Other' ? form.heard_about_other : form.heard_about,
        linkedin_website: [form.linkedin, form.website].filter(Boolean).join(' | '),
      };
      const res = await axios.post(`${API}/open-letter/sign`, payload);
      if (res.data.success) {
        setSubmitted(true);
        // Route to contextual support page after a short delay so the user briefly sees the confirmation
        setTimeout(() => {
          navigate('/support?source=open_letter&next=/open-letter');
        }, 1500);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Submission failed. Please try again.';
      toast.error(msg);
    }
    setIsSubmitting(false);
  };

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div data-testid="open-letter-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-16 md:py-28">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-400 mb-4">Open Letter</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white mb-8">
              Calling for Independent Verification Standards for AI Systems Interacting with Children
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-10">
              A Call for Independent Accountability in Child-Facing Artificial Intelligence.
            </p>
            <button onClick={scrollToForm} className="btn-primary-light" data-testid="btn-become-signatory">
              Become a Signatory
            </button>
          </div>
        </div>
      </section>

      {/* The Letter */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-3xl mx-auto container-padding">
          {/* Letter header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">OPEN LETTER</h2>
            <p className="text-xl md:text-2xl font-semibold text-slate-700">Calling for Independent Verification Standards for AI Systems Interacting with Children</p>
          </div>

          <div className="space-y-6 text-lg text-slate-700 leading-relaxed">
            <p className="text-slate-900 font-semibold text-xl">A Call for Independent Accountability in Child-Facing Artificial Intelligence.</p>

            <p>Artificial intelligence systems are now embedded into environments used daily by children.</p>

            <p>They are present in education platforms, entertainment systems, social applications, gaming ecosystems, communication tools, digital assistants, recommendation systems, and emerging AI companions.</p>

            <p>These systems influence behaviour, emotional development, relationships, identity formation, learning patterns, decision-making, trust, and exposure to harm at unprecedented scale.</p>

            <p className="text-slate-900 font-semibold">Yet despite the speed and scale of deployment, there is currently no globally recognised independent verification framework specifically designed to assess the behavioural safety of AI systems interacting with children.</p>

            <p>In most cases, the organisations developing these systems:</p>

            <div className="space-y-2 pl-6 border-l-4 border-slate-900">
              <p>define their own safety standards,</p>
              <p>conduct their own internal assessments,</p>
              <p>and publicly declare compliance themselves.</p>
            </div>

            <p className="text-slate-900 font-semibold">No mature high-risk industry accepts this model as sufficient where human safety is involved.</p>

            <p>In aviation, pharmaceuticals, medicine, electrical systems, finance, transportation, and construction, independent verification exists because self-assessment alone is not considered credible protection against systemic harm.</p>

            <p className="text-slate-900 font-semibold">Child safety should not be held to a lower standard than aviation software, medical devices, or electrical infrastructure.</p>

            <p>Yet today, systems capable of shaping a child's beliefs, behaviours, emotional state, vulnerabilities, and worldview are being deployed globally without independent behavioural certification infrastructure.</p>

            <p className="text-slate-900 font-semibold">This represents a structural governance gap with potentially generational consequences.</p>

            {/* The Need for Independent Verification */}
            <h3 className="text-2xl font-bold text-slate-900 pt-6">The Need for Independent Verification</h3>

            <p>The rapid integration of AI into child-facing environments now requires the development of robust independent oversight systems capable of evaluating behavioural risk, safety performance, and long-term societal impact.</p>

            <p>We believe this must include:</p>

            <div className="space-y-3 pl-6">
              {[
                'Independent behavioural safety verification frameworks',
                'Standardised behavioural risk testing protocols',
                'Ongoing external auditing and reassessment',
                'Transparent evidence-based certification processes',
                'Structural separation between developers, standards bodies, and certifiers',
                'Public accountability mechanisms for safety failures and systemic harms',
                'International coordination on child-facing AI governance standards',
              ].map((p) => (
                <div key={p} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-cyan-700 mt-2.5 flex-shrink-0"></div>
                  <p>{p}</p>
                </div>
              ))}
            </div>

            <p>Safety claims cannot rely solely on internal policies, marketing language, voluntary principles, or platform self-attestation.</p>

            <p className="text-slate-900 font-semibold">Verification must become independent, measurable, transparent, and enforceable.</p>

            {/* This Is Not Opposition to Innovation */}
            <h3 className="text-2xl font-bold text-slate-900 pt-6">This Is Not Opposition to Innovation</h3>

            <p>This is not a call to halt innovation.</p>

            <p>It is a call to mature the accountability infrastructure surrounding technologies capable of influencing children at global scale.</p>

            <p>History shows that transformative technologies inevitably require independent oversight once their societal impact reaches critical mass.</p>

            <p className="text-slate-900 font-semibold">Artificial intelligence is no exception.</p>

            <p>The question is no longer whether AI systems will interact with children.</p>

            <p className="text-slate-900 font-semibold">They already do.</p>

            <p>The question is whether independent accountability systems will exist before irreversible harm scales faster than oversight.</p>

            {/* A Call to Governments, Institutions, and Industry */}
            <h3 className="text-2xl font-bold text-slate-900 pt-6">A Call to Governments, Institutions, and Industry</h3>

            <p>We therefore call on:</p>

            <p className="text-slate-600">
              governments, regulators, educational institutions, technology companies, standards organisations, academic researchers, child safety advocates, civil society leaders, and international policy bodies
            </p>

            <p>to support the urgent development of internationally recognised independent verification standards for AI systems interacting with children.</p>

            <p className="text-slate-900 font-semibold">Systems capable of influencing children at scale should not operate without independent scrutiny.</p>

            <p>The builder does not certify.</p>

            <p className="text-slate-900 font-semibold">Safety without independent verification is not safety.</p>

            {/* Proposed Outcome */}
            <h3 className="text-2xl font-bold text-slate-900 pt-6">Proposed Outcome</h3>

            <p>We support the establishment of an independent international standards and verification consortium tasked with developing:</p>

            <div className="space-y-3 pl-6">
              {[
                'enforceable behavioural safety testing protocols,',
                'certification frameworks,',
                'audit and reassessment requirements,',
                'transparency obligations,',
                'and ongoing compliance standards',
              ].map((p) => (
                <div key={p} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-cyan-700 mt-2.5 flex-shrink-0"></div>
                  <p>{p}</p>
                </div>
              ))}
            </div>

            <p>for AI systems operating within child-facing environments.</p>

            <p className="text-slate-900 font-semibold">Independent verification must become foundational infrastructure for child-facing artificial intelligence.</p>

            <div className="border-t border-slate-200 pt-8 mt-8">
              <p className="text-slate-900 font-semibold text-xl">Future generations should not inherit systems that were scaled before meaningful safeguards existed.</p>
              <p className="text-slate-700 mt-4">History will not judge whether warnings existed.</p>
              <p className="text-slate-900 font-semibold mt-2">It will judge whether institutions acted before the damage became systemic.</p>
            </div>
          </div>

          {/* Signatories — single running ruled-paper list (founding + public) */}
          <div className="mt-16">
            <div className="flex items-baseline justify-between mb-6">
              <p className="text-xs uppercase tracking-widest text-slate-500">Signed</p>
              <p className="text-xs text-slate-400" data-testid="signatory-count">
                {FOUNDING_SIGNATORIES.length + signatories.length} {FOUNDING_SIGNATORIES.length + signatories.length === 1 ? 'signatory' : 'signatories'}
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 px-6 sm:px-8 pt-3 pb-6">
              {[
                ...FOUNDING_SIGNATORIES,
                ...signatories.map((s) => ({
                  key: s.id,
                  name: `${s.first_name} ${s.last_name}`.trim(),
                  detail: [s.organization, s.country].filter(Boolean).join(' — '),
                })),
              ].map((s, i) => (
                <div
                  key={s.key || `founding-${i}`}
                  className="flex items-end border-b border-slate-300/70"
                  style={{ height: '40px', paddingBottom: '7px' }}
                  data-testid="signatory-line"
                >
                  <p
                    className="text-base text-slate-900 truncate"
                    style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', lineHeight: 1 }}
                  >
                    <span className="font-semibold">{s.name}</span>
                    {s.detail ? ` — ${s.detail}` : ''}
                  </p>
                </div>
              ))}
              <p className="text-sm text-cyan-700 mt-5">safestandard.ai</p>
            </div>
          </div>
        </div>
      </section>

      {/* Public signatories are merged into the single "Signed" list above */}

      {/* Signatory Form */}
      <section className="section-padding bg-white border-t border-slate-200" ref={formRef}>
        <div className="max-w-3xl mx-auto container-padding">
          {submitted ? (
            <div className="text-center py-12">
              <h2 className="heading-2 mb-6">Thank You</h2>
              <p className="text-lg text-slate-700 mb-4">
                Thank you for supporting the Safe Standard Open Letter.
              </p>
              <p className="text-lg text-slate-700 mb-4">
                Your submission has been received successfully.
              </p>
              <p className="text-sm text-slate-500 mb-10">
                Where permission has been granted, approved signatories may appear publicly on the signatories section of the website following moderation and verification.
              </p>

              <div className="border-t border-slate-200 pt-8">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-6">Share your support</p>
                <div className="flex flex-wrap justify-center gap-3">
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://safestandard.ai/open-letter')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#0A66C2] text-white px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
                    data-testid="share-linkedin"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    LinkedIn
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('I have signed the Open Letter calling for independent AI safety standards and verification. Join us: https://safestandard.ai/open-letter')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-slate-900 text-white px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
                    data-testid="share-x"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    X / Twitter
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://safestandard.ai/open-letter')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#1877F2] text-white px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
                    data-testid="share-facebook"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    Facebook
                  </a>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText('https://safestandard.ai/open-letter');
                      toast.success('Link copied to clipboard');
                    }}
                    className="bg-slate-200 text-slate-900 px-6 py-3 text-sm font-medium hover:bg-slate-300 transition-colors flex items-center gap-2"
                    data-testid="share-copy"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                    Copy Link
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <p className="caption text-cyan-700 mb-4">Become a Signatory</p>
              <p className="text-lg text-slate-700 mb-4">
                By signing this Open Letter, you voluntarily express support for the principles outlined above and for the advancement of independent AI safety and governance frameworks.
              </p>
              <p className="text-sm text-slate-500 mb-10">
                Signing this letter does not create legal obligations, certification status, regulatory authority, or institutional endorsement unless explicitly authorised.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="signatory-form">
                {/* Required fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">First Name *</label>
                    <input type="text" required value={form.first_name} onChange={(e) => updateForm('first_name', e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900" data-testid="sig-first-name" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Last Name *</label>
                    <input type="text" required value={form.last_name} onChange={(e) => updateForm('last_name', e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900" data-testid="sig-last-name" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Email Address *</label>
                    <input type="email" required value={form.email} onChange={(e) => updateForm('email', e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900" data-testid="sig-email" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Country *</label>
                    <input type="text" required value={form.country} onChange={(e) => updateForm('country', e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900" data-testid="sig-country" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Mobile Phone (Optional)</label>
                  <div className="flex gap-2">
                    <select value={form.phone_code} onChange={(e) => updateForm('phone_code', e.target.value)}
                      className="w-32 px-2 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900 bg-white">
                      <option value="+93">+93 AF</option>
                      <option value="+355">+355 AL</option>
                      <option value="+213">+213 DZ</option>
                      <option value="+376">+376 AD</option>
                      <option value="+244">+244 AO</option>
                      <option value="+54">+54 AR</option>
                      <option value="+374">+374 AM</option>
                      <option value="+61">+61 AU</option>
                      <option value="+43">+43 AT</option>
                      <option value="+994">+994 AZ</option>
                      <option value="+973">+973 BH</option>
                      <option value="+880">+880 BD</option>
                      <option value="+375">+375 BY</option>
                      <option value="+32">+32 BE</option>
                      <option value="+501">+501 BZ</option>
                      <option value="+229">+229 BJ</option>
                      <option value="+975">+975 BT</option>
                      <option value="+591">+591 BO</option>
                      <option value="+387">+387 BA</option>
                      <option value="+267">+267 BW</option>
                      <option value="+55">+55 BR</option>
                      <option value="+673">+673 BN</option>
                      <option value="+359">+359 BG</option>
                      <option value="+226">+226 BF</option>
                      <option value="+257">+257 BI</option>
                      <option value="+855">+855 KH</option>
                      <option value="+237">+237 CM</option>
                      <option value="+1">+1 CA</option>
                      <option value="+238">+238 CV</option>
                      <option value="+236">+236 CF</option>
                      <option value="+235">+235 TD</option>
                      <option value="+56">+56 CL</option>
                      <option value="+86">+86 CN</option>
                      <option value="+57">+57 CO</option>
                      <option value="+269">+269 KM</option>
                      <option value="+243">+243 CD</option>
                      <option value="+242">+242 CG</option>
                      <option value="+506">+506 CR</option>
                      <option value="+385">+385 HR</option>
                      <option value="+53">+53 CU</option>
                      <option value="+357">+357 CY</option>
                      <option value="+420">+420 CZ</option>
                      <option value="+45">+45 DK</option>
                      <option value="+253">+253 DJ</option>
                      <option value="+593">+593 EC</option>
                      <option value="+20">+20 EG</option>
                      <option value="+503">+503 SV</option>
                      <option value="+240">+240 GQ</option>
                      <option value="+291">+291 ER</option>
                      <option value="+372">+372 EE</option>
                      <option value="+251">+251 ET</option>
                      <option value="+679">+679 FJ</option>
                      <option value="+358">+358 FI</option>
                      <option value="+33">+33 FR</option>
                      <option value="+241">+241 GA</option>
                      <option value="+220">+220 GM</option>
                      <option value="+995">+995 GE</option>
                      <option value="+49">+49 DE</option>
                      <option value="+233">+233 GH</option>
                      <option value="+30">+30 GR</option>
                      <option value="+502">+502 GT</option>
                      <option value="+224">+224 GN</option>
                      <option value="+592">+592 GY</option>
                      <option value="+509">+509 HT</option>
                      <option value="+504">+504 HN</option>
                      <option value="+852">+852 HK</option>
                      <option value="+36">+36 HU</option>
                      <option value="+354">+354 IS</option>
                      <option value="+91">+91 IN</option>
                      <option value="+62">+62 ID</option>
                      <option value="+98">+98 IR</option>
                      <option value="+964">+964 IQ</option>
                      <option value="+353">+353 IE</option>
                      <option value="+972">+972 IL</option>
                      <option value="+39">+39 IT</option>
                      <option value="+225">+225 CI</option>
                      <option value="+876">+876 JM</option>
                      <option value="+81">+81 JP</option>
                      <option value="+962">+962 JO</option>
                      <option value="+7">+7 KZ</option>
                      <option value="+254">+254 KE</option>
                      <option value="+965">+965 KW</option>
                      <option value="+996">+996 KG</option>
                      <option value="+856">+856 LA</option>
                      <option value="+371">+371 LV</option>
                      <option value="+961">+961 LB</option>
                      <option value="+266">+266 LS</option>
                      <option value="+231">+231 LR</option>
                      <option value="+218">+218 LY</option>
                      <option value="+423">+423 LI</option>
                      <option value="+370">+370 LT</option>
                      <option value="+352">+352 LU</option>
                      <option value="+261">+261 MG</option>
                      <option value="+265">+265 MW</option>
                      <option value="+60">+60 MY</option>
                      <option value="+960">+960 MV</option>
                      <option value="+223">+223 ML</option>
                      <option value="+356">+356 MT</option>
                      <option value="+222">+222 MR</option>
                      <option value="+230">+230 MU</option>
                      <option value="+52">+52 MX</option>
                      <option value="+373">+373 MD</option>
                      <option value="+377">+377 MC</option>
                      <option value="+976">+976 MN</option>
                      <option value="+382">+382 ME</option>
                      <option value="+212">+212 MA</option>
                      <option value="+258">+258 MZ</option>
                      <option value="+95">+95 MM</option>
                      <option value="+264">+264 NA</option>
                      <option value="+977">+977 NP</option>
                      <option value="+31">+31 NL</option>
                      <option value="+64">+64 NZ</option>
                      <option value="+505">+505 NI</option>
                      <option value="+227">+227 NE</option>
                      <option value="+234">+234 NG</option>
                      <option value="+850">+850 KP</option>
                      <option value="+389">+389 MK</option>
                      <option value="+47">+47 NO</option>
                      <option value="+968">+968 OM</option>
                      <option value="+92">+92 PK</option>
                      <option value="+507">+507 PA</option>
                      <option value="+675">+675 PG</option>
                      <option value="+595">+595 PY</option>
                      <option value="+51">+51 PE</option>
                      <option value="+63">+63 PH</option>
                      <option value="+48">+48 PL</option>
                      <option value="+351">+351 PT</option>
                      <option value="+974">+974 QA</option>
                      <option value="+40">+40 RO</option>
                      <option value="+7">+7 RU</option>
                      <option value="+250">+250 RW</option>
                      <option value="+966">+966 SA</option>
                      <option value="+221">+221 SN</option>
                      <option value="+381">+381 RS</option>
                      <option value="+232">+232 SL</option>
                      <option value="+65">+65 SG</option>
                      <option value="+421">+421 SK</option>
                      <option value="+386">+386 SI</option>
                      <option value="+252">+252 SO</option>
                      <option value="+27">+27 ZA</option>
                      <option value="+82">+82 KR</option>
                      <option value="+211">+211 SS</option>
                      <option value="+34">+34 ES</option>
                      <option value="+94">+94 LK</option>
                      <option value="+249">+249 SD</option>
                      <option value="+597">+597 SR</option>
                      <option value="+46">+46 SE</option>
                      <option value="+41">+41 CH</option>
                      <option value="+963">+963 SY</option>
                      <option value="+886">+886 TW</option>
                      <option value="+992">+992 TJ</option>
                      <option value="+255">+255 TZ</option>
                      <option value="+66">+66 TH</option>
                      <option value="+228">+228 TG</option>
                      <option value="+676">+676 TO</option>
                      <option value="+216">+216 TN</option>
                      <option value="+90">+90 TR</option>
                      <option value="+993">+993 TM</option>
                      <option value="+256">+256 UG</option>
                      <option value="+380">+380 UA</option>
                      <option value="+971">+971 AE</option>
                      <option value="+44">+44 GB</option>
                      <option value="+1">+1 US</option>
                      <option value="+598">+598 UY</option>
                      <option value="+998">+998 UZ</option>
                      <option value="+58">+58 VE</option>
                      <option value="+84">+84 VN</option>
                      <option value="+967">+967 YE</option>
                      <option value="+260">+260 ZM</option>
                      <option value="+263">+263 ZW</option>
                    </select>
                    <input type="tel" value={form.phone} onChange={(e) => updateForm('phone', e.target.value)}
                      placeholder="Phone number"
                      className="flex-1 px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900" />
                  </div>
                </div>

                {/* Optional fields */}
                <div className="border-t border-slate-100 pt-6">
                  <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">Optional</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Organisation</label>
                      <input type="text" value={form.organization} onChange={(e) => updateForm('organization', e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Role / Title</label>
                      <input type="text" value={form.role} onChange={(e) => updateForm('role', e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Industry / Sector</label>
                      <select value={form.industry} onChange={(e) => updateForm('industry', e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900 bg-white">
                        <option value="">Select...</option>
                        <option value="Technology Platform">Technology Platform</option>
                        <option value="Government">Government</option>
                        <option value="University / Academic">University / Academic</option>
                        <option value="Research Institution">Research Institution</option>
                        <option value="Non-Profit / NGO">Non-Profit / NGO</option>
                        <option value="Education / EdTech">Education / EdTech</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Legal / Regulatory">Legal / Regulatory</option>
                        <option value="Insurance / Risk">Insurance / Risk</option>
                        <option value="Financial Services">Financial Services</option>
                        <option value="Child Safety / Protection">Child Safety / Protection</option>
                        <option value="Human Rights / Civil Society">Human Rights / Civil Society</option>
                        <option value="Media / Communications">Media / Communications</option>
                        <option value="Cybersecurity">Cybersecurity</option>
                        <option value="Standards Body">Standards Body</option>
                        <option value="Consulting / Advisory">Consulting / Advisory</option>
                        <option value="Defence / National Security">Defence / National Security</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">LinkedIn Profile</label>
                      <input type="text" value={form.linkedin} onChange={(e) => updateForm('linkedin', e.target.value)}
                        placeholder="linkedin.com/in/yourname"
                        className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900" />
                    </div>
                  </div>
                  {form.industry === 'Other' && (
                    <div className="mt-4">
                      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Please Specify</label>
                      <input type="text" value={form.industry_other} onChange={(e) => updateForm('industry_other', e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900" />
                    </div>
                  )}
                  <div className="mt-4">
                    <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Website</label>
                    <input type="text" value={form.website} onChange={(e) => updateForm('website', e.target.value)}
                      placeholder="yourwebsite.com"
                      className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900" />
                  </div>
                  <div className="mt-4">
                    <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Why are you signing?</label>
                    <textarea value={form.why_signing} onChange={(e) => updateForm('why_signing', e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900 h-20 resize-none" />
                  </div>
                  <div className="mt-4">
                    <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">How did you hear about us?</label>
                    <select value={form.heard_about} onChange={(e) => updateForm('heard_about', e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900 bg-white">
                      <option value="">Select...</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="X / Twitter">X / Twitter</option>
                      <option value="Facebook">Facebook</option>
                      <option value="Google Search">Google Search</option>
                      <option value="News Article">News Article</option>
                      <option value="Industry Conference">Industry Conference</option>
                      <option value="Colleague / Word of Mouth">Colleague / Word of Mouth</option>
                      <option value="Council Member Referral">Council Member Referral</option>
                      <option value="Academic Publication">Academic Publication</option>
                      <option value="Government / Regulatory Body">Government / Regulatory Body</option>
                      <option value="Podcast / Interview">Podcast / Interview</option>
                      <option value="Newsletter / Email">Newsletter / Email</option>
                      <option value="YouTube / Video">YouTube / Video</option>
                      <option value="Professional Network">Professional Network</option>
                      <option value="Child Safety Organisation">Child Safety Organisation</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  {form.heard_about === 'Other' && (
                    <div className="mt-4">
                      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Please Specify</label>
                      <input type="text" value={form.heard_about_other} onChange={(e) => updateForm('heard_about_other', e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900" />
                    </div>
                  )}
                </div>

                {/* Select all + Required checkboxes */}
                <div className="border-t border-slate-100 pt-6 space-y-4">
                  <label className="flex items-start gap-3 cursor-pointer bg-slate-50 p-3 border border-slate-200">
                    <input type="checkbox"
                      checked={form.confirm_read && form.confirm_no_legal && form.confirm_data && form.public_display && form.receive_updates}
                      onChange={(e) => {
                        const val = e.target.checked;
                        setForm(prev => ({ ...prev, confirm_read: val, confirm_no_legal: val, confirm_data: val, public_display: val, receive_updates: val }));
                      }}
                      className="mt-1 w-4 h-4 flex-shrink-0" data-testid="sig-select-all" />
                    <span className="text-sm text-slate-900 font-medium">Select all</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.confirm_read} onChange={(e) => updateForm('confirm_read', e.target.checked)}
                      className="mt-1 w-4 h-4 flex-shrink-0" data-testid="sig-confirm-read" />
                    <span className="text-sm text-slate-700">I confirm that I have read the Open Letter and voluntarily support the principles outlined within it. *</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.confirm_no_legal} onChange={(e) => updateForm('confirm_no_legal', e.target.checked)}
                      className="mt-1 w-4 h-4 flex-shrink-0" data-testid="sig-confirm-legal" />
                    <span className="text-sm text-slate-700">I understand that signing this Open Letter does not create any legal, financial, regulatory, or contractual relationship with Safe Standard. *</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.confirm_data} onChange={(e) => updateForm('confirm_data', e.target.checked)}
                      className="mt-1 w-4 h-4 flex-shrink-0" data-testid="sig-confirm-data" />
                    <span className="text-sm text-slate-700">I consent to Safe Standard storing and processing my submitted information for the purpose of administering this initiative. *</span>
                  </label>
                </div>

                {/* Optional checkboxes */}
                <div className="space-y-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.public_display} onChange={(e) => updateForm('public_display', e.target.checked)}
                      className="mt-1 w-4 h-4 flex-shrink-0" />
                    <span className="text-sm text-slate-500">I consent to my name, organisation, country, and submitted professional information being publicly displayed as a signatory where applicable.</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.receive_updates} onChange={(e) => updateForm('receive_updates', e.target.checked)}
                      className="mt-1 w-4 h-4 flex-shrink-0" />
                    <span className="text-sm text-slate-500">I would like to receive updates regarding Safe Standard initiatives, publications, consultations, and future developments.</span>
                  </label>
                </div>

                {/* Legal links */}
                <div className="border-t border-slate-100 pt-6">
                  <p className="text-sm text-slate-500">
                    By submitting this form, you acknowledge that you have read and agree to the{' '}
                    <button type="button" onClick={() => setShowLegal(true)} className="text-cyan-700 hover:text-cyan-900 underline">Legal Disclaimer</button>
                    {' '}and{' '}
                    <button type="button" onClick={() => setShowPrivacy(true)} className="text-cyan-700 hover:text-cyan-900 underline">Privacy Notice</button>.
                  </p>
                </div>

                <button type="submit" disabled={isSubmitting} className="btn-primary w-full" data-testid="sig-submit">
                  {isSubmitting ? 'Submitting...' : 'Sign the Open Letter'}
                </button>
              </form>
            </>
          )}
        </div>
      </section>

      {/* Legal Disclaimer Modal */}
      <LegalModal isOpen={showLegal} onClose={() => setShowLegal(false)} title="Legal Disclaimer">
        <h3 className="font-semibold text-slate-900">Independent Initiative</h3>
        <p>Safe Standard is an independent initiative focused on advancing AI governance, assurance, and verification concepts. Safe Standard is not a governmental authority, regulatory agency, accredited standards organisation, or certification body unless explicitly stated otherwise.</p>

        <h3 className="font-semibold text-slate-900 mt-4">No Certification or Regulatory Authority</h3>
        <p>Participation in this Open Letter does not constitute certification, legal compliance validation, regulatory approval, or endorsement by any government or standards body.</p>

        <h3 className="font-semibold text-slate-900 mt-4">Individual Capacity</h3>
        <p>Unless explicitly stated otherwise, individuals signing this Open Letter do so in their personal capacity and do not necessarily represent the views of their employers, affiliated organisations, governments, or institutions.</p>

        <h3 className="font-semibold text-slate-900 mt-4">No Legal Relationship</h3>
        <p>Signing this Open Letter does not create any partnership, contractual obligation, fiduciary duty, membership entitlement, or legal relationship between Safe Standard and any signatory.</p>

        <h3 className="font-semibold text-slate-900 mt-4">Public Display</h3>
        <p>Only information for which explicit public-display consent has been granted may appear publicly on the website or related materials.</p>

        <h3 className="font-semibold text-slate-900 mt-4">Right to Moderate</h3>
        <p>Safe Standard reserves the right to review, approve, reject, categorise, edit formatting, or remove submissions at its discretion.</p>

        <h3 className="font-semibold text-slate-900 mt-4">Future Statements</h3>
        <p>References to future governance frameworks, standards, verification systems, or certification models reflect conceptual objectives and current intentions only and should not be interpreted as existing regulatory authority or internationally recognised accreditation.</p>
      </LegalModal>

      {/* Privacy Notice Modal */}
      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Privacy Notice">
        <p>Submitted information may be securely stored and used for:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>administration of the Open Letter initiative,</li>
          <li>signatory verification,</li>
          <li>communications related to Safe Standard,</li>
          <li>and public display where permission has been granted.</li>
        </ul>
        <p className="mt-4">Safe Standard does not sell personal information to third parties.</p>
        <p className="mt-4">Users may request removal of their information at any time by contacting:</p>
        <p className="text-cyan-700 font-medium">support@safestandard.ai</p>
      </LegalModal>
    </div>
  );
};

export default OpenLetterPage;
